import React, { useState, useCallback, useMemo } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { RefreshCw, Download, ChevronDown, FileText } from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { CARBON_BTN_OUTLINE } from './carbon/carbonClasses';

// CHA-394 — accent hexes for the six state families (kept as ink accents / left rails, never
// pastel fills). Blue prod / green invoice / purple left-to-ship / orange pick / teal ATS are the
// Carbon tokens; WIP is deliberately Tailwind amber-500 (not Carbon yellow) so the rail matches
// the preserved text-amber-500/700 column colors.
const ACCENT = {
  prod: '#0F62FE',
  invoice: '#24A148',
  lts: '#8A3FFC',
  pick: '#FF832B',
  ats: '#007D79',
  wip: '#F59E0B',
};

// Generic Production vs Sales Hybrid Dashboard
// Reusable for any customer (Walmart, Target, Sam's, etc.)
// Props: customerName, customers, divisions, labels, defaultExcludeLabels, excludedPrograms, logoRenderer, brandColor
export default function ProductionSalesHybrid({
  customerName = 'Customer',
  customers = [],
  divisions = ['10'],
  labels = [],
  defaultExcludeLabels = [],
  excludedPrograms = [],
  logoRenderer = null,
  brandColor = { primary: '#0071ce', bg: 'blue' },
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [departmentData, setDepartmentData] = useState([]);
  const [deptProgramData, setDeptProgramData] = useState([]);
  const [styleDetails, setStyleDetails] = useState([]);
  const [missingFinelines, setMissingFinelines] = useState([]);
  const [filters, setFilters] = useState({ labels: [], seasons: [] });

  const [selectedSeason, setSelectedSeason] = useState('SP26');
  const [selectedDivision, setSelectedDivision] = useState(divisions[0] || '10');
  const [excludeLabels, setExcludeLabels] = useState(defaultExcludeLabels);
  const [excludeAllYear, setExcludeAllYear] = useState(false);
  const [groupBy, setGroupBy] = useState('Program');
  const [showLabelDropdown, setShowLabelDropdown] = useState(false);

  const calcGP = (rev, cost, royalty = 0) => rev > 0 ? (rev - cost - royalty) / rev : 0;
  const formatCurrency = (v) => {
    const n = Number(v) || 0;
    if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
    if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
    return `$${n.toFixed(0)}`;
  };
  const formatPct = (v) => `${(Number(v) * 100).toFixed(1)}%`;
  const gpColor = (gp) => gp >= 0.3 ? 'text-green-600' : gp < 0 ? 'text-red-600' : 'text-carbon-gray-70';

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'getProductionSalesInventory', { timeout: 150000 });
      const [result, deptResult, deptProgResult] = await Promise.all([
        fn({
          seasons: [selectedSeason],
          customers,
          divisions: [selectedDivision],
          excludeLabels,
          excludeAllYear,
          groupBy
        }),
        groupBy !== 'Department' ? fn({
          seasons: [selectedSeason],
          customers,
          divisions: [selectedDivision],
          excludeLabels,
          excludeAllYear,
          groupBy: 'Department'
        }) : Promise.resolve(null),
        fn({
          seasons: [selectedSeason],
          customers,
          divisions: [selectedDivision],
          excludeLabels,
          excludeAllYear,
          groupBy: 'DepartmentProgram'
        })
      ]);
      setData(result.data.summary || []);
      setDepartmentData(groupBy !== 'Department' ? (deptResult?.data?.summary || []) : []);
      setDeptProgramData(deptProgResult?.data?.summary || []);
      setStyleDetails(deptProgResult?.data?.styleDetails || []);
      setMissingFinelines(groupBy !== 'Department' ? (deptResult?.data?.missingFinelines || []) : (result.data.missingFinelines || []));
      setFilters(result.data.filters || {});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedSeason, selectedDivision, excludeLabels, excludeAllYear, groupBy]);

  const r = (row, f) => Number(row[f]) || 0;

  const filteredData = useMemo(() => data.filter(row => !excludedPrograms.includes(row.group_key)), [data, excludedPrograms]);
  const excludedData = useMemo(() => data.filter(row => excludedPrograms.includes(row.group_key)), [data, excludedPrograms]);

  const totals = useMemo(() => {
    return filteredData.reduce((acc, row) => {
      const fields = [
        'total_prod_rev', 'total_prod_cost_fob', 'total_prod_royalty',
        'received_rev', 'received_cost_fob', 'received_royalty',
        'wip_transit_rev', 'wip_transit_cost_fob', 'wip_transit_royalty',
        'wip_open_rev', 'wip_open_cost_fob', 'wip_open_royalty',
        'wip_rev', 'wip_cost_fob', 'wip_royalty',
        'invoiced_rev', 'invoiced_cost', 'invoiced_royalty',
        'pick_rev', 'pick_cost', 'pick_royalty',
        'open_rev', 'open_cost',
        'ats_rev', 'ats_cost', 'ats_royalty',
        'oversold_rev',
      ];
      fields.forEach(f => { acc[f] = (acc[f] || 0) + (Number(row[f]) || 0); });
      return acc;
    }, {});
  }, [filteredData]);

  const ltsRev = totals.total_prod_rev - totals.invoiced_rev;
  const ltsCostFob = totals.total_prod_cost_fob - totals.invoiced_cost;
  const ltsRoyalty = (totals.total_prod_royalty || 0) - (totals.invoiced_royalty || 0);

  const availableLabels = [
    ...(filters.labels || []).filter(l =>
      labels.some(wl => l.toUpperCase().includes(wl.toUpperCase()))
    ),
    '(No Label)'
  ];

  const handleExport = () => {
    const rows = filteredData.map(row => ({
      [groupBy]: row.group_key || '-',
      'Total Prod $': r(row, 'total_prod_rev'),
      'Total Prod FOB': r(row, 'total_prod_cost_fob'),
      'Total Prod Royalty': r(row, 'total_prod_royalty'),
      'GP%': calcGP(r(row, 'total_prod_rev'), r(row, 'total_prod_cost_fob'), r(row, 'total_prod_royalty')),
      'Invoiced $': r(row, 'invoiced_rev'),
      'Invoiced Cost': r(row, 'invoiced_cost'),
      'Invoiced Royalty': r(row, 'invoiced_royalty'),
      'Inv GP%': calcGP(r(row, 'invoiced_rev'), r(row, 'invoiced_cost'), r(row, 'invoiced_royalty')),
      'Pick $': r(row, 'pick_rev'),
      'Pick Cost': r(row, 'pick_cost'),
      'Pick Royalty': r(row, 'pick_royalty'),
      'Pick GP%': calcGP(r(row, 'pick_rev'), r(row, 'pick_cost'), r(row, 'pick_royalty')),
      'ATS $': r(row, 'ats_rev'),
      'ATS Cost': r(row, 'ats_cost'),
      'ATS Royalty': r(row, 'ats_royalty'),
      'ATS GP%': calcGP(r(row, 'ats_rev'), r(row, 'ats_cost'), r(row, 'ats_royalty')),
      'WIP $': r(row, 'wip_rev'),
      'WIP FOB': r(row, 'wip_cost_fob'),
      'WIP Royalty': r(row, 'wip_royalty'),
      'WIP GP%': calcGP(r(row, 'wip_rev'), r(row, 'wip_cost_fob'), r(row, 'wip_royalty')),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tables');

    // Details tab - style-level with fineline/department mapping
    if (styleDetails.length > 0) {
      const detailRows = styleDetails.map(d => ({
        'Dept Status': d.Dept_Status,
        'Department': d.Department || '(Missing)',
        'Program': d.Program,
        'Style': d.Style,
        'Color': d.Color,
        'Label': d.Label,
        'Fine Line': d.Fine_Line,
      }));
      const wsDetails = XLSX.utils.json_to_sheet(detailRows);
      XLSX.utils.book_append_sheet(wb, wsDetails, 'Details');
    }

    XLSX.writeFile(wb, `${customerName}_ProdSales_Hybrid_${selectedSeason}_Div${selectedDivision}.xlsx`);
  };

  const filteredDeptData = useMemo(() => departmentData.filter(row => !excludedPrograms.includes(row.group_key)), [departmentData, excludedPrograms]);

  const deptTotals = useMemo(() => {
    return filteredDeptData.reduce((acc, row) => {
      ['total_prod_rev','total_prod_cost_fob','total_prod_royalty','invoiced_rev','invoiced_cost','invoiced_royalty','pick_rev','pick_cost','pick_royalty','ats_rev','ats_cost','ats_royalty','wip_rev','wip_cost_fob','wip_royalty'].forEach(f => { acc[f] = (acc[f] || 0) + (Number(row[f]) || 0); });
      return acc;
    }, {});
  }, [filteredDeptData]);

  const handleExportPDF = () => {
    const deptRows = filteredDeptData;

    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    if (logoRenderer) {
      logoRenderer(doc);
    }

    doc.setFontSize(16);
    doc.setTextColor(0, 113, 206);
    doc.text(`${customerName} Production vs Sales - ${selectedSeason} Div ${selectedDivision}`, 18, 15);

    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    const footnotes = [];
    if (excludeLabels.length > 0) footnotes.push(`Excludes labels: ${excludeLabels.join(', ')}`);
    if (excludeAllYear) footnotes.push('Excludes All Year styles');
    if (excludedData.length > 0) footnotes.push(`Excluded from totals: ${excludedData.map(rd => `${rd.group_key} (${formatCurrency(r(rd, 'total_prod_rev'))})`).join(', ')} — under research`);
    footnotes.forEach((fn, i) => doc.text(fn, 14, 21 + i * 4));

    doc.setTextColor(150, 150, 150);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 21 + footnotes.length * 4);

    const cardY = 25 + footnotes.length * 4;
    const cardH = 18;
    const cardGap = 3;
    const cardW = (pageWidth - 28 - cardGap * 5) / 6;
    const cards = [
      { label: 'Total Production', value: formatCurrency(totals.total_prod_rev), gp: formatPct(calcGP(totals.total_prod_rev, totals.total_prod_cost_fob, totals.total_prod_royalty)), bg: [219, 234, 254] },
      { label: 'Invoice', value: formatCurrency(totals.invoiced_rev), gp: formatPct(calcGP(totals.invoiced_rev, totals.invoiced_cost, totals.invoiced_royalty)), bg: [220, 252, 231] },
      { label: 'Left to Ship', value: formatCurrency(ltsRev), gp: formatPct(calcGP(ltsRev, ltsCostFob, ltsRoyalty)), bg: [243, 232, 255] },
      { label: 'On Pick', value: formatCurrency(totals.pick_rev), gp: formatPct(calcGP(totals.pick_rev, totals.pick_cost, totals.pick_royalty)), bg: [255, 237, 213] },
      { label: 'ATS', value: formatCurrency(totals.ats_rev), gp: formatPct(calcGP(totals.ats_rev, totals.ats_cost, totals.ats_royalty)), bg: [204, 251, 241] },
      { label: 'Total WIP', value: formatCurrency(totals.wip_rev), gp: formatPct(calcGP(totals.wip_rev, totals.wip_cost_fob, totals.wip_royalty)), bg: [255, 251, 235] },
    ];
    cards.forEach((card, i) => {
      const x = 14 + i * (cardW + cardGap);
      doc.setFillColor(...card.bg);
      doc.roundedRect(x, cardY, cardW, cardH, 2, 2, 'F');
      doc.setFontSize(7);
      doc.setTextColor(80, 80, 80);
      doc.text(card.label, x + cardW / 2, cardY + 5, { align: 'center' });
      doc.setFontSize(12);
      doc.setTextColor(30, 30, 30);
      doc.text(card.value, x + cardW / 2, cardY + 11, { align: 'center' });
      doc.setFontSize(7);
      doc.setTextColor(100, 100, 100);
      doc.text(`${card.gp} GP`, x + cardW / 2, cardY + 15, { align: 'center' });
    });

    const tableStartY = cardY + cardH + 8;
    doc.setFontSize(11);
    doc.setTextColor(30, 30, 30);
    doc.text(`By ${groupBy}`, 14, tableStartY);

    const headers = [[groupBy, 'Total Cost', 'Total Prod $', 'GP%', 'Inv Total Cost', 'Invoice $', 'GP%', 'Pick Total Cost', 'Pick $', 'GP%', 'ATS Total Cost', 'ATS $', 'GP%', 'WIP Total Cost', 'WIP $', 'GP%']];

    const mapRow = (row) => {
      const tp = r(row, 'total_prod_rev'), tc = r(row, 'total_prod_cost_fob'), tr = r(row, 'total_prod_royalty');
      const ir = r(row, 'invoiced_rev'), ic = r(row, 'invoiced_cost'), iry = r(row, 'invoiced_royalty');
      const pr = r(row, 'pick_rev'), pc = r(row, 'pick_cost'), pry = r(row, 'pick_royalty');
      const ar = r(row, 'ats_rev'), ac = r(row, 'ats_cost'), ary = r(row, 'ats_royalty');
      const wr = r(row, 'wip_rev'), wc = r(row, 'wip_cost_fob'), wry = r(row, 'wip_royalty');
      return [
        row.group_key || '-',
        formatCurrency(tc + tr), formatCurrency(tp), formatPct(calcGP(tp, tc, tr)),
        formatCurrency(ic + iry), formatCurrency(ir), formatPct(calcGP(ir, ic, iry)),
        formatCurrency(pc + pry), formatCurrency(pr), formatPct(calcGP(pr, pc, pry)),
        formatCurrency(ac + ary), formatCurrency(ar), formatPct(calcGP(ar, ac, ary)),
        formatCurrency(wc + wry), formatCurrency(wr), formatPct(calcGP(wr, wc, wry)),
      ];
    };

    const totalRow = [
      'TOTAL',
      formatCurrency((totals.total_prod_cost_fob || 0) + (totals.total_prod_royalty || 0)), formatCurrency(totals.total_prod_rev), formatPct(calcGP(totals.total_prod_rev, totals.total_prod_cost_fob, totals.total_prod_royalty)),
      formatCurrency((totals.invoiced_cost || 0) + (totals.invoiced_royalty || 0)), formatCurrency(totals.invoiced_rev), formatPct(calcGP(totals.invoiced_rev, totals.invoiced_cost, totals.invoiced_royalty)),
      formatCurrency((totals.pick_cost || 0) + (totals.pick_royalty || 0)), formatCurrency(totals.pick_rev), formatPct(calcGP(totals.pick_rev, totals.pick_cost, totals.pick_royalty)),
      formatCurrency((totals.ats_cost || 0) + (totals.ats_royalty || 0)), formatCurrency(totals.ats_rev), formatPct(calcGP(totals.ats_rev, totals.ats_cost, totals.ats_royalty)),
      formatCurrency((totals.wip_cost_fob || 0) + (totals.wip_royalty || 0)), formatCurrency(totals.wip_rev), formatPct(calcGP(totals.wip_rev, totals.wip_cost_fob, totals.wip_royalty)),
    ];

    autoTable(doc, {
      startY: tableStartY + 4,
      margin: { left: 14, right: 14 },
      head: headers,
      body: [...filteredData.map(mapRow), totalRow],
      theme: 'grid',
      styles: { fontSize: 6.5, cellPadding: 1.5, lineColor: [147, 171, 219], lineWidth: 0.3 },
      headStyles: { fillColor: [37, 99, 235], textColor: 255, fontSize: 6.5, lineColor: [37, 99, 235] },
      alternateRowStyles: { fillColor: [245, 247, 255] },
      columnStyles: {
        0: { cellWidth: 28 },
        1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' },
        4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' },
        7: { halign: 'right' }, 8: { halign: 'right' }, 9: { halign: 'right' },
        10: { halign: 'right' }, 11: { halign: 'right' }, 12: { halign: 'right' },
        13: { halign: 'right' }, 14: { halign: 'right' }, 15: { halign: 'right' },
      },
      didParseCell: (data) => {
        if (data.row.index === filteredData.length && data.section === 'body') {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [226, 232, 240];
        }
      },
    });

    const finalY = doc.lastAutoTable.finalY;
    doc.setDrawColor(147, 171, 219);
    doc.setLineWidth(0.5);
    doc.roundedRect(13, tableStartY + 3, pageWidth - 26, finalY - tableStartY - 1, 3, 3, 'S');

    doc.setFontSize(7);
    doc.setTextColor(180, 180, 180);
    doc.text('Cost includes Royalty', pageWidth - 14, pageHeight - 12, { align: 'right' });
    doc.text('This report is still in development mode', pageWidth - 14, pageHeight - 8, { align: 'right' });

    // Page 2: Department table
    if (deptRows.length > 0 && groupBy !== 'Department') {
      doc.addPage();
      doc.setFontSize(11);
      doc.setTextColor(30, 30, 30);
      doc.text('By Department', 14, 15);

      const deptHeaders = [['Department', 'Total Cost', 'Total Prod $', 'GP%', 'Inv Total Cost', 'Invoice $', 'GP%', 'Pick Total Cost', 'Pick $', 'GP%', 'ATS Total Cost', 'ATS $', 'GP%', 'WIP Total Cost', 'WIP $', 'GP%']];

      const mapDeptRow = (row) => {
        const tp = r(row, 'total_prod_rev'), tc = r(row, 'total_prod_cost_fob'), tr2 = r(row, 'total_prod_royalty');
        const ir = r(row, 'invoiced_rev'), ic = r(row, 'invoiced_cost'), iry = r(row, 'invoiced_royalty');
        const pr = r(row, 'pick_rev'), pc = r(row, 'pick_cost'), pry = r(row, 'pick_royalty');
        const ar = r(row, 'ats_rev'), ac = r(row, 'ats_cost'), ary = r(row, 'ats_royalty');
        const wr = r(row, 'wip_rev'), wc = r(row, 'wip_cost_fob'), wry = r(row, 'wip_royalty');
        return [
          row.group_key || '-',
          formatCurrency(tc + tr2), formatCurrency(tp), formatPct(calcGP(tp, tc, tr2)),
          formatCurrency(ic + iry), formatCurrency(ir), formatPct(calcGP(ir, ic, iry)),
          formatCurrency(pc + pry), formatCurrency(pr), formatPct(calcGP(pr, pc, pry)),
          formatCurrency(ac + ary), formatCurrency(ar), formatPct(calcGP(ar, ac, ary)),
          formatCurrency(wc + wry), formatCurrency(wr), formatPct(calcGP(wr, wc, wry)),
        ];
      };

      const deptTotals = deptRows.reduce((acc, row) => {
        ['total_prod_rev','total_prod_cost_fob','total_prod_royalty','invoiced_rev','invoiced_cost','invoiced_royalty','pick_rev','pick_cost','pick_royalty','ats_rev','ats_cost','ats_royalty','wip_rev','wip_cost_fob','wip_royalty'].forEach(f => { acc[f] = (acc[f] || 0) + (Number(row[f]) || 0); });
        return acc;
      }, {});

      const dt = deptTotals;
      const deptTotalRow = [
        'TOTAL',
        formatCurrency((dt.total_prod_cost_fob || 0) + (dt.total_prod_royalty || 0)), formatCurrency(dt.total_prod_rev), formatPct(calcGP(dt.total_prod_rev, dt.total_prod_cost_fob, dt.total_prod_royalty)),
        formatCurrency((dt.invoiced_cost || 0) + (dt.invoiced_royalty || 0)), formatCurrency(dt.invoiced_rev), formatPct(calcGP(dt.invoiced_rev, dt.invoiced_cost, dt.invoiced_royalty)),
        formatCurrency((dt.pick_cost || 0) + (dt.pick_royalty || 0)), formatCurrency(dt.pick_rev), formatPct(calcGP(dt.pick_rev, dt.pick_cost, dt.pick_royalty)),
        formatCurrency((dt.ats_cost || 0) + (dt.ats_royalty || 0)), formatCurrency(dt.ats_rev), formatPct(calcGP(dt.ats_rev, dt.ats_cost, dt.ats_royalty)),
        formatCurrency((dt.wip_cost_fob || 0) + (dt.wip_royalty || 0)), formatCurrency(dt.wip_rev), formatPct(calcGP(dt.wip_rev, dt.wip_cost_fob, dt.wip_royalty)),
      ];

      autoTable(doc, {
        startY: 19,
        margin: { left: 14, right: 14 },
        head: deptHeaders,
        body: [...deptRows.map(mapDeptRow), deptTotalRow],
        theme: 'grid',
        styles: { fontSize: 6.5, cellPadding: 1.5, lineColor: [147, 171, 219], lineWidth: 0.3 },
        headStyles: { fillColor: [79, 70, 229], textColor: 255, fontSize: 6.5, lineColor: [79, 70, 229] },
        alternateRowStyles: { fillColor: [245, 245, 255] },
        columnStyles: {
          0: { cellWidth: 28 },
          1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' },
          4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' },
          7: { halign: 'right' }, 8: { halign: 'right' }, 9: { halign: 'right' },
          10: { halign: 'right' }, 11: { halign: 'right' }, 12: { halign: 'right' },
          13: { halign: 'right' }, 14: { halign: 'right' }, 15: { halign: 'right' },
        },
        didParseCell: (data) => {
          if (data.row.index === deptRows.length && data.section === 'body') {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [226, 232, 240];
          }
        },
      });

      const deptFinalY = doc.lastAutoTable.finalY;
      doc.setDrawColor(147, 171, 219);
      doc.setLineWidth(0.5);
      doc.roundedRect(13, 18, pageWidth - 26, deptFinalY - 16, 3, 3, 'S');

      if (missingFinelines.length > 0) {
        doc.setFontSize(7);
        doc.setTextColor(180, 120, 0);
        doc.text(`Finelines missing department mapping (${missingFinelines.length}): ${missingFinelines.join(', ')}`, 14, deptFinalY + 6);
      }
    }

    // Page 3: Department / Program nested table
    if (deptProgramData.length > 0) {
      const dpMap = {};
      deptProgramData.forEach(row => {
        const parts = (row.group_key || '').split('|');
        const dept = parts[0] || 'Unknown';
        const program = parts[1] || '-';
        if (!dpMap[dept]) dpMap[dept] = [];
        dpMap[dept].push({ ...row, _dept: dept, _program: program });
      });
      const dpSorted = Object.entries(dpMap).sort((a, b) => {
        const aT = a[1].reduce((s, rw) => s + (Number(rw.total_prod_rev) || 0), 0);
        const bT = b[1].reduce((s, rw) => s + (Number(rw.total_prod_rev) || 0), 0);
        return bT - aT;
      });

      doc.addPage();
      doc.setFontSize(11);
      doc.setTextColor(30, 30, 30);
      doc.text('By Department / Program', 14, 15);

      const dpHeaders = [['Dept / Program', 'Total Prod $', 'GP%', 'Invoice $', 'GP%', 'Pick $', 'GP%', 'ATS $', 'GP%', 'WIP $', 'GP%']];

      const dpBody = [];
      dpSorted.forEach(([dept, rows]) => {
        const sorted = [...rows].sort((a, b) => (Number(b.total_prod_rev) || 0) - (Number(a.total_prod_rev) || 0));
        // Dept subtotal
        const sub = ['total_prod_rev','total_prod_cost_fob','total_prod_royalty','invoiced_rev','invoiced_cost','invoiced_royalty','pick_rev','pick_cost','pick_royalty','ats_rev','ats_cost','ats_royalty','wip_rev','wip_cost_fob','wip_royalty'].reduce((acc, f) => { acc[f] = rows.reduce((s, rw) => s + (Number(rw[f]) || 0), 0); return acc; }, {});
        dpBody.push({
          cells: [
            `Dept ${dept}`,
            formatCurrency(sub.total_prod_rev), formatPct(calcGP(sub.total_prod_rev, sub.total_prod_cost_fob, sub.total_prod_royalty)),
            formatCurrency(sub.invoiced_rev), formatPct(calcGP(sub.invoiced_rev, sub.invoiced_cost, sub.invoiced_royalty)),
            formatCurrency(sub.pick_rev), formatPct(calcGP(sub.pick_rev, sub.pick_cost, sub.pick_royalty)),
            formatCurrency(sub.ats_rev), formatPct(calcGP(sub.ats_rev, sub.ats_cost, sub.ats_royalty)),
            formatCurrency(sub.wip_rev), formatPct(calcGP(sub.wip_rev, sub.wip_cost_fob, sub.wip_royalty)),
          ], isDept: true
        });
        sorted.forEach(rw => {
          const tp = Number(rw.total_prod_rev)||0, tc = Number(rw.total_prod_cost_fob)||0, tr3 = Number(rw.total_prod_royalty)||0;
          const ir2 = Number(rw.invoiced_rev)||0, ic2 = Number(rw.invoiced_cost)||0, iry2 = Number(rw.invoiced_royalty)||0;
          const pr2 = Number(rw.pick_rev)||0, pc2 = Number(rw.pick_cost)||0, pry2 = Number(rw.pick_royalty)||0;
          const ar2 = Number(rw.ats_rev)||0, ac2 = Number(rw.ats_cost)||0, ary2 = Number(rw.ats_royalty)||0;
          const wr2 = Number(rw.wip_rev)||0, wc2 = Number(rw.wip_cost_fob)||0, wry2 = Number(rw.wip_royalty)||0;
          dpBody.push({
            cells: [
              `    ${rw._program}`,
              formatCurrency(tp), formatPct(calcGP(tp, tc, tr3)),
              formatCurrency(ir2), formatPct(calcGP(ir2, ic2, iry2)),
              formatCurrency(pr2), formatPct(calcGP(pr2, pc2, pry2)),
              formatCurrency(ar2), formatPct(calcGP(ar2, ac2, ary2)),
              formatCurrency(wr2), formatPct(calcGP(wr2, wc2, wry2)),
            ], isDept: false
          });
        });
      });

      // Grand total
      const dpGrand = ['total_prod_rev','total_prod_cost_fob','total_prod_royalty','invoiced_rev','invoiced_cost','invoiced_royalty','pick_rev','pick_cost','pick_royalty','ats_rev','ats_cost','ats_royalty','wip_rev','wip_cost_fob','wip_royalty'].reduce((acc, f) => { acc[f] = deptProgramData.reduce((s, rw) => s + (Number(rw[f]) || 0), 0); return acc; }, {});
      dpBody.push({
        cells: [
          'TOTAL',
          formatCurrency(dpGrand.total_prod_rev), formatPct(calcGP(dpGrand.total_prod_rev, dpGrand.total_prod_cost_fob, dpGrand.total_prod_royalty)),
          formatCurrency(dpGrand.invoiced_rev), formatPct(calcGP(dpGrand.invoiced_rev, dpGrand.invoiced_cost, dpGrand.invoiced_royalty)),
          formatCurrency(dpGrand.pick_rev), formatPct(calcGP(dpGrand.pick_rev, dpGrand.pick_cost, dpGrand.pick_royalty)),
          formatCurrency(dpGrand.ats_rev), formatPct(calcGP(dpGrand.ats_rev, dpGrand.ats_cost, dpGrand.ats_royalty)),
          formatCurrency(dpGrand.wip_rev), formatPct(calcGP(dpGrand.wip_rev, dpGrand.wip_cost_fob, dpGrand.wip_royalty)),
        ], isDept: true, isTotal: true
      });

      autoTable(doc, {
        startY: 19,
        margin: { left: 14, right: 14 },
        head: dpHeaders,
        body: dpBody.map(r => r.cells),
        theme: 'grid',
        styles: { fontSize: 6.5, cellPadding: 1.5, lineColor: [147, 171, 219], lineWidth: 0.3 },
        headStyles: { fillColor: [124, 58, 237], textColor: 255, fontSize: 6.5, lineColor: [124, 58, 237] },
        columnStyles: {
          0: { cellWidth: 35 },
          1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' },
          4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' },
          7: { halign: 'right' }, 8: { halign: 'right' }, 9: { halign: 'right' },
          10: { halign: 'right' },
        },
        didParseCell: (data) => {
          if (data.section === 'body') {
            const rowInfo = dpBody[data.row.index];
            if (rowInfo?.isTotal) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [226, 232, 240];
            } else if (rowInfo?.isDept) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [241, 241, 250];
            }
          }
        },
      });
    }

    doc.save(`${customerName}_ProdSales_${selectedSeason}_Div${selectedDivision}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // CHA-394 — per-tile-border stat tile (CarbonStatStrip idiom): mono eyebrow label, mono
  // numerals, 3px left rail in the state family's accent color instead of a pastel fill.
  const SummaryCard = ({ label, rev, cost, royalty = 0, accent }) => (
    <div className="bg-carbon-white border border-carbon-border border-l-[3px] px-3 py-2.5" style={{ borderLeftColor: accent }}>
      <p className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50">{label}</p>
      <p className="font-plex-mono text-lg font-semibold text-carbon-ink tabular-nums mt-0.5">{formatCurrency(rev)}</p>
      <p className="font-plex-mono text-[10px] text-carbon-gray-50 tabular-nums">COST {formatCurrency(cost + royalty)}</p>
      <p className={`font-plex-mono text-[11px] tabular-nums ${gpColor(calcGP(rev, cost, royalty))}`}>{formatPct(calcGP(rev, cost, royalty))} GP</p>
    </div>
  );

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-plex-mono text-[10px] tracking-[0.14em] uppercase text-carbon-gray-50">Cut &amp; Sold</p>
          <h2 className="text-xl font-semibold text-carbon-ink tracking-[-0.01em]">{customerName} Production vs Sales</h2>
          <p className="font-plex-mono text-[10px] text-carbon-gray-50 mt-0.5">Production tables for quantities + Inventory for sales breakdown. GP% = (Price_A - WAC - Royalty) / Price_A</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleExportPDF} disabled={data.length === 0}
            className={`flex items-center gap-1.5 ${CARBON_BTN_OUTLINE}`}>
            <FileText size={12} /> PDF
          </button>
          <button onClick={handleExport} disabled={data.length === 0}
            className={`flex items-center gap-1.5 ${CARBON_BTN_OUTLINE}`}>
            <Download size={12} /> Excel
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap bg-carbon-white border border-carbon-border p-3">
        <div className="flex items-center gap-1.5">
          <label className="font-plex-mono text-[10px] tracking-[0.1em] uppercase text-carbon-gray-50">Season</label>
          <select value={selectedSeason} onChange={(e) => setSelectedSeason(e.target.value)}
            className="text-xs border border-carbon-gray-30 bg-carbon-white text-carbon-ink px-2 py-1 focus:outline-none focus:ring-1 focus:ring-carbon-blue-60">
            {/* CHA-305 — seasons come from the SERVER's filters (the hardcoded trio hid SP27),
                UNIONED with the known floor: a degenerate server list (the original self-referential
                season-scoped response) must never strand the dropdown at one option. New seasons
                appear from the server with zero code changes; the selected season always stays listed. */}
            {[...new Set([
              selectedSeason,
              ...(filters.seasons || []),
              'SP27', 'SP26', 'FA25', 'SP25',
            ])].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <label className="font-plex-mono text-[10px] tracking-[0.1em] uppercase text-carbon-gray-50">Division</label>
          <select value={selectedDivision} onChange={(e) => setSelectedDivision(e.target.value)}
            className="text-xs border border-carbon-gray-30 bg-carbon-white text-carbon-ink px-2 py-1 focus:outline-none focus:ring-1 focus:ring-carbon-blue-60">
            {divisions.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <label className="font-plex-mono text-[10px] tracking-[0.1em] uppercase text-carbon-gray-50">Group By</label>
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}
            className="text-xs border border-carbon-gray-30 bg-carbon-white text-carbon-ink px-2 py-1 focus:outline-none focus:ring-1 focus:ring-carbon-blue-60">
            <option value="Program">Program</option>
            <option value="Department">Department</option>
            <option value="Customer">Customer</option>
            <option value="Label">Label</option>
            <option value="COO">Country</option>
            <option value="Division">Division</option>
          </select>
        </div>
        <div className="relative">
          <button onClick={() => setShowLabelDropdown(!showLabelDropdown)}
            className="flex items-center gap-1 text-xs border border-carbon-gray-30 px-2 py-1 bg-carbon-white text-carbon-ink hover:bg-carbon-gray-10">
            Exclude Labels {excludeLabels.length > 0 && `(${excludeLabels.length})`} <ChevronDown size={10} />
          </button>
          {showLabelDropdown && (
            <div className="absolute top-full left-0 mt-1 bg-carbon-white border border-carbon-border z-50 p-2 min-w-[160px]">
              {availableLabels.map(label => (
                <label key={label} className="flex items-center gap-2 px-2 py-1 text-xs text-carbon-ink hover:bg-carbon-gray-10 cursor-pointer">
                  <input type="checkbox" checked={excludeLabels.includes(label)}
                    onChange={(e) => setExcludeLabels(prev =>
                      e.target.checked ? [...prev, label] : prev.filter(l => l !== label)
                    )} />
                  {label}
                </label>
              ))}
              <button onClick={() => setShowLabelDropdown(false)}
                className="mt-1 w-full text-xs text-carbon-blue-60 hover:underline">Done</button>
            </div>
          )}
        </div>
        <label className="flex items-center gap-1.5 text-xs text-carbon-ink">
          <input type="checkbox" checked={excludeAllYear} onChange={(e) => setExcludeAllYear(e.target.checked)} />
          Exclude All Year
        </label>
        <button onClick={fetchData} disabled={loading}
          className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium bg-carbon-blue-60 text-white hover:bg-carbon-blue-70 disabled:opacity-50">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          {loading ? 'Loading...' : 'Load Data'}
        </button>
      </div>

      {error && <div className="bg-carbon-white border border-carbon-border border-l-[3px] border-l-[#DA1E28] p-3 text-sm text-carbon-ink">{error}</div>}

      {data.length > 0 && (
        <>
          <div>
            <p className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50 mb-2">{customerName} {selectedSeason} · Div {selectedDivision} · Summary</p>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
              <SummaryCard label="Total Production" rev={totals.total_prod_rev} cost={totals.total_prod_cost_fob} royalty={totals.total_prod_royalty} accent={ACCENT.prod} />
              <SummaryCard label="Invoice" rev={totals.invoiced_rev} cost={totals.invoiced_cost} royalty={totals.invoiced_royalty} accent={ACCENT.invoice} />
              <SummaryCard label="Left to Ship" rev={ltsRev} cost={ltsCostFob} royalty={ltsRoyalty} accent={ACCENT.lts} />
              <SummaryCard label="On Pick" rev={totals.pick_rev} cost={totals.pick_cost} royalty={totals.pick_royalty} accent={ACCENT.pick} />
              <SummaryCard label="ATS" rev={totals.ats_rev} cost={totals.ats_cost} royalty={totals.ats_royalty} accent={ACCENT.ats} />
              <SummaryCard label="Total WIP" rev={totals.wip_rev} cost={totals.wip_cost_fob} royalty={totals.wip_royalty} accent={ACCENT.wip} />
            </div>
          </div>

          <div className="bg-carbon-white border border-carbon-border overflow-hidden">
            <div className="px-4 py-3 bg-carbon-gray-10 border-b border-carbon-border">
              <h3 className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold text-carbon-ink">By {groupBy}</h3>
              <p className="font-plex-mono text-[10px] text-carbon-gray-50 mt-0.5">{filteredData.length} rows</p>
            </div>
            <div className="overflow-x-auto max-h-[500px]">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-carbon-gray-10 z-10">
                  <tr>
                    <th className="px-3 py-2 text-left font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">{groupBy}</th>
                    <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-blue-500 border-b border-carbon-border">Total Cost</th>
                    <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-blue-700 border-b border-carbon-border">Total Prod $</th>
                    <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-blue-600 border-b border-carbon-border">GP%</th>
                    <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-green-500 border-b border-carbon-border">Inv Total Cost</th>
                    <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-green-700 border-b border-carbon-border">Invoice $</th>
                    <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-green-600 border-b border-carbon-border">GP%</th>
                    <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-orange-500 border-b border-carbon-border">Pick Total Cost</th>
                    <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-orange-700 border-b border-carbon-border">Pick $</th>
                    <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-orange-600 border-b border-carbon-border">GP%</th>
                    <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-teal-500 border-b border-carbon-border">ATS Total Cost</th>
                    <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-teal-700 border-b border-carbon-border">ATS $</th>
                    <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-teal-600 border-b border-carbon-border">GP%</th>
                    <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-amber-500 border-b border-carbon-border">WIP Total Cost</th>
                    <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-amber-700 border-b border-carbon-border">WIP $</th>
                    <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-amber-600 border-b border-carbon-border">GP%</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((row, idx) => {
                    const totalRev = r(row, 'total_prod_rev');
                    const totalCostFob = r(row, 'total_prod_cost_fob');
                    const totalRoyalty = r(row, 'total_prod_royalty');
                    const invRev = r(row, 'invoiced_rev');
                    const invCost = r(row, 'invoiced_cost');
                    const invRoyalty = r(row, 'invoiced_royalty');
                    const pickRev = r(row, 'pick_rev');
                    const pickCost = r(row, 'pick_cost');
                    const pickRoyalty = r(row, 'pick_royalty');
                    const atsRev = r(row, 'ats_rev');
                    const atsCost = r(row, 'ats_cost');
                    const atsRoyalty = r(row, 'ats_royalty');
                    const wipRev = r(row, 'wip_rev');
                    const wipCostFob = r(row, 'wip_cost_fob');
                    const wipRoyalty = r(row, 'wip_royalty');
                    return (
                      <tr key={idx} className="border-b border-carbon-gray-10 hover:bg-carbon-gray-10">
                        <td className="px-3 py-2 font-medium text-carbon-ink truncate max-w-[160px]">{row.group_key || '-'}</td>
                        <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-blue-500">{formatCurrency(totalCostFob + totalRoyalty)}</td>
                        <td className="px-2 py-2 text-right font-plex-mono tabular-nums font-medium text-blue-700">{formatCurrency(totalRev)}</td>
                        <td className={`px-2 py-2 text-right font-plex-mono tabular-nums ${gpColor(calcGP(totalRev, totalCostFob, totalRoyalty))}`}>{formatPct(calcGP(totalRev, totalCostFob, totalRoyalty))}</td>
                        <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-green-500">{formatCurrency(invCost + invRoyalty)}</td>
                        <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-green-700">{formatCurrency(invRev)}</td>
                        <td className={`px-2 py-2 text-right font-plex-mono tabular-nums ${gpColor(calcGP(invRev, invCost, invRoyalty))}`}>{formatPct(calcGP(invRev, invCost, invRoyalty))}</td>
                        <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-orange-500">{formatCurrency(pickCost + pickRoyalty)}</td>
                        <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-orange-700">{formatCurrency(pickRev)}</td>
                        <td className={`px-2 py-2 text-right font-plex-mono tabular-nums ${gpColor(calcGP(pickRev, pickCost, pickRoyalty))}`}>{formatPct(calcGP(pickRev, pickCost, pickRoyalty))}</td>
                        <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-teal-500">{formatCurrency(atsCost + atsRoyalty)}</td>
                        <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-teal-700">{formatCurrency(atsRev)}</td>
                        <td className={`px-2 py-2 text-right font-plex-mono tabular-nums ${gpColor(calcGP(atsRev, atsCost, atsRoyalty))}`}>{formatPct(calcGP(atsRev, atsCost, atsRoyalty))}</td>
                        <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-amber-500">{formatCurrency(wipCostFob + wipRoyalty)}</td>
                        <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-amber-700">{formatCurrency(wipRev)}</td>
                        <td className={`px-2 py-2 text-right font-plex-mono tabular-nums ${gpColor(calcGP(wipRev, wipCostFob, wipRoyalty))}`}>{formatPct(calcGP(wipRev, wipCostFob, wipRoyalty))}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="sticky bottom-0 bg-carbon-gray-10 border-t-2 border-carbon-border">
                  <tr className="font-semibold text-carbon-ink">
                    <td className="px-3 py-2">TOTAL</td>
                    <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-blue-500">{formatCurrency(totals.total_prod_cost_fob + totals.total_prod_royalty)}</td>
                    <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-blue-700">{formatCurrency(totals.total_prod_rev)}</td>
                    <td className={`px-2 py-2 text-right font-plex-mono tabular-nums ${gpColor(calcGP(totals.total_prod_rev, totals.total_prod_cost_fob, totals.total_prod_royalty))}`}>{formatPct(calcGP(totals.total_prod_rev, totals.total_prod_cost_fob, totals.total_prod_royalty))}</td>
                    <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-green-500">{formatCurrency((totals.invoiced_cost || 0) + (totals.invoiced_royalty || 0))}</td>
                    <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-green-700">{formatCurrency(totals.invoiced_rev)}</td>
                    <td className={`px-2 py-2 text-right font-plex-mono tabular-nums ${gpColor(calcGP(totals.invoiced_rev, totals.invoiced_cost, totals.invoiced_royalty))}`}>{formatPct(calcGP(totals.invoiced_rev, totals.invoiced_cost, totals.invoiced_royalty))}</td>
                    <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-orange-500">{formatCurrency((totals.pick_cost || 0) + (totals.pick_royalty || 0))}</td>
                    <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-orange-700">{formatCurrency(totals.pick_rev)}</td>
                    <td className={`px-2 py-2 text-right font-plex-mono tabular-nums ${gpColor(calcGP(totals.pick_rev, totals.pick_cost, totals.pick_royalty))}`}>{formatPct(calcGP(totals.pick_rev, totals.pick_cost, totals.pick_royalty))}</td>
                    <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-teal-500">{formatCurrency((totals.ats_cost || 0) + (totals.ats_royalty || 0))}</td>
                    <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-teal-700">{formatCurrency(totals.ats_rev)}</td>
                    <td className={`px-2 py-2 text-right font-plex-mono tabular-nums ${gpColor(calcGP(totals.ats_rev, totals.ats_cost, totals.ats_royalty))}`}>{formatPct(calcGP(totals.ats_rev, totals.ats_cost, totals.ats_royalty))}</td>
                    <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-amber-500">{formatCurrency(totals.wip_cost_fob + totals.wip_royalty)}</td>
                    <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-amber-700">{formatCurrency(totals.wip_rev)}</td>
                    <td className={`px-2 py-2 text-right font-plex-mono tabular-nums ${gpColor(calcGP(totals.wip_rev, totals.wip_cost_fob, totals.wip_royalty))}`}>{formatPct(calcGP(totals.wip_rev, totals.wip_cost_fob, totals.wip_royalty))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="mt-1 px-3 space-y-0.5">
              <div className="flex justify-between">
                <div className="space-y-0.5">
                  {excludeLabels.length > 0 && (
                    <p className="font-plex-mono text-[10px] text-carbon-gray-50">* Excludes labels: {excludeLabels.join(', ')}</p>
                  )}
                  {excludeAllYear && (
                    <p className="font-plex-mono text-[10px] text-carbon-gray-50">* Excludes All Year styles</p>
                  )}
                  {excludedData.length > 0 && (
                    <p className="font-plex-mono text-[10px] text-amber-600">* Excluded from totals: {excludedData.map(r => `${r.group_key} (${formatCurrency(r.total_prod_rev)})`).join(', ')} — under research</p>
                  )}
                </div>
                <p className="font-plex-mono text-[10px] text-carbon-gray-50">Cost includes Royalty</p>
              </div>
            </div>
          </div>

          {/* Department Table - always shown when groupBy is not Department */}
          {filteredDeptData.length > 0 && groupBy !== 'Department' && (
            <div className="bg-carbon-white border border-carbon-border overflow-hidden">
              <div className="px-4 py-3 bg-carbon-gray-10 border-b border-carbon-border">
                <h3 className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold text-carbon-ink">By Department</h3>
                <p className="font-plex-mono text-[10px] text-carbon-gray-50 mt-0.5">{filteredDeptData.length} rows</p>
              </div>
              <div className="overflow-x-auto max-h-[400px]">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-carbon-gray-10 z-10">
                    <tr>
                      <th className="px-3 py-2 text-left font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">Department</th>
                      <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-blue-500 border-b border-carbon-border">Total Cost</th>
                      <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-blue-700 border-b border-carbon-border">Total Prod $</th>
                      <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-blue-600 border-b border-carbon-border">GP%</th>
                      <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-green-500 border-b border-carbon-border">Inv Total Cost</th>
                      <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-green-700 border-b border-carbon-border">Invoice $</th>
                      <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-green-600 border-b border-carbon-border">GP%</th>
                      <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-orange-500 border-b border-carbon-border">Pick Total Cost</th>
                      <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-orange-700 border-b border-carbon-border">Pick $</th>
                      <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-orange-600 border-b border-carbon-border">GP%</th>
                      <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-teal-500 border-b border-carbon-border">ATS Total Cost</th>
                      <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-teal-700 border-b border-carbon-border">ATS $</th>
                      <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-teal-600 border-b border-carbon-border">GP%</th>
                      <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-amber-500 border-b border-carbon-border">WIP Total Cost</th>
                      <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-amber-700 border-b border-carbon-border">WIP $</th>
                      <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-amber-600 border-b border-carbon-border">GP%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDeptData.map((row, idx) => {
                      const totalRev = r(row, 'total_prod_rev'), totalCostFob = r(row, 'total_prod_cost_fob'), totalRoyalty = r(row, 'total_prod_royalty');
                      const invRev = r(row, 'invoiced_rev'), invCost = r(row, 'invoiced_cost'), invRoyalty = r(row, 'invoiced_royalty');
                      const pickRev = r(row, 'pick_rev'), pickCost = r(row, 'pick_cost'), pickRoyalty = r(row, 'pick_royalty');
                      const atsRev = r(row, 'ats_rev'), atsCost = r(row, 'ats_cost'), atsRoyalty = r(row, 'ats_royalty');
                      const wipRev = r(row, 'wip_rev'), wipCostFob = r(row, 'wip_cost_fob'), wipRoyalty = r(row, 'wip_royalty');
                      return (
                        <tr key={idx} className="border-b border-carbon-gray-10 hover:bg-carbon-gray-10">
                          <td className="px-3 py-2 font-medium text-carbon-ink">{row.group_key || '-'}</td>
                          <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-blue-500">{formatCurrency(totalCostFob + totalRoyalty)}</td>
                          <td className="px-2 py-2 text-right font-plex-mono tabular-nums font-medium text-blue-700">{formatCurrency(totalRev)}</td>
                          <td className={`px-2 py-2 text-right font-plex-mono tabular-nums ${gpColor(calcGP(totalRev, totalCostFob, totalRoyalty))}`}>{formatPct(calcGP(totalRev, totalCostFob, totalRoyalty))}</td>
                          <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-green-500">{formatCurrency(invCost + invRoyalty)}</td>
                          <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-green-700">{formatCurrency(invRev)}</td>
                          <td className={`px-2 py-2 text-right font-plex-mono tabular-nums ${gpColor(calcGP(invRev, invCost, invRoyalty))}`}>{formatPct(calcGP(invRev, invCost, invRoyalty))}</td>
                          <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-orange-500">{formatCurrency(pickCost + pickRoyalty)}</td>
                          <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-orange-700">{formatCurrency(pickRev)}</td>
                          <td className={`px-2 py-2 text-right font-plex-mono tabular-nums ${gpColor(calcGP(pickRev, pickCost, pickRoyalty))}`}>{formatPct(calcGP(pickRev, pickCost, pickRoyalty))}</td>
                          <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-teal-500">{formatCurrency(atsCost + atsRoyalty)}</td>
                          <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-teal-700">{formatCurrency(atsRev)}</td>
                          <td className={`px-2 py-2 text-right font-plex-mono tabular-nums ${gpColor(calcGP(atsRev, atsCost, atsRoyalty))}`}>{formatPct(calcGP(atsRev, atsCost, atsRoyalty))}</td>
                          <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-amber-500">{formatCurrency(wipCostFob + wipRoyalty)}</td>
                          <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-amber-700">{formatCurrency(wipRev)}</td>
                          <td className={`px-2 py-2 text-right font-plex-mono tabular-nums ${gpColor(calcGP(wipRev, wipCostFob, wipRoyalty))}`}>{formatPct(calcGP(wipRev, wipCostFob, wipRoyalty))}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="sticky bottom-0 bg-carbon-gray-10 border-t-2 border-carbon-border">
                    <tr className="font-semibold text-carbon-ink">
                      <td className="px-3 py-2">TOTAL</td>
                      <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-blue-500">{formatCurrency((deptTotals.total_prod_cost_fob || 0) + (deptTotals.total_prod_royalty || 0))}</td>
                      <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-blue-700">{formatCurrency(deptTotals.total_prod_rev)}</td>
                      <td className={`px-2 py-2 text-right font-plex-mono tabular-nums ${gpColor(calcGP(deptTotals.total_prod_rev, deptTotals.total_prod_cost_fob, deptTotals.total_prod_royalty))}`}>{formatPct(calcGP(deptTotals.total_prod_rev, deptTotals.total_prod_cost_fob, deptTotals.total_prod_royalty))}</td>
                      <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-green-500">{formatCurrency((deptTotals.invoiced_cost || 0) + (deptTotals.invoiced_royalty || 0))}</td>
                      <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-green-700">{formatCurrency(deptTotals.invoiced_rev)}</td>
                      <td className={`px-2 py-2 text-right font-plex-mono tabular-nums ${gpColor(calcGP(deptTotals.invoiced_rev, deptTotals.invoiced_cost, deptTotals.invoiced_royalty))}`}>{formatPct(calcGP(deptTotals.invoiced_rev, deptTotals.invoiced_cost, deptTotals.invoiced_royalty))}</td>
                      <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-orange-500">{formatCurrency((deptTotals.pick_cost || 0) + (deptTotals.pick_royalty || 0))}</td>
                      <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-orange-700">{formatCurrency(deptTotals.pick_rev)}</td>
                      <td className={`px-2 py-2 text-right font-plex-mono tabular-nums ${gpColor(calcGP(deptTotals.pick_rev, deptTotals.pick_cost, deptTotals.pick_royalty))}`}>{formatPct(calcGP(deptTotals.pick_rev, deptTotals.pick_cost, deptTotals.pick_royalty))}</td>
                      <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-teal-500">{formatCurrency((deptTotals.ats_cost || 0) + (deptTotals.ats_royalty || 0))}</td>
                      <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-teal-700">{formatCurrency(deptTotals.ats_rev)}</td>
                      <td className={`px-2 py-2 text-right font-plex-mono tabular-nums ${gpColor(calcGP(deptTotals.ats_rev, deptTotals.ats_cost, deptTotals.ats_royalty))}`}>{formatPct(calcGP(deptTotals.ats_rev, deptTotals.ats_cost, deptTotals.ats_royalty))}</td>
                      <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-amber-500">{formatCurrency((deptTotals.wip_cost_fob || 0) + (deptTotals.wip_royalty || 0))}</td>
                      <td className="px-2 py-2 text-right font-plex-mono tabular-nums text-amber-700">{formatCurrency(deptTotals.wip_rev)}</td>
                      <td className={`px-2 py-2 text-right font-plex-mono tabular-nums ${gpColor(calcGP(deptTotals.wip_rev, deptTotals.wip_cost_fob, deptTotals.wip_royalty))}`}>{formatPct(calcGP(deptTotals.wip_rev, deptTotals.wip_cost_fob, deptTotals.wip_royalty))}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              {missingFinelines.length > 0 && (
                <div className="px-3 py-2 bg-carbon-white border-t border-carbon-border border-l-[3px] border-l-[#F59E0B]">
                  <p className="font-plex-mono text-[11px] text-carbon-ink font-medium">Finelines missing department mapping ({missingFinelines.length}): <span className="font-normal text-carbon-gray-70">{missingFinelines.join(', ')}</span></p>
                </div>
              )}
            </div>
          )}

          {/* Department / Program Nested Table */}
          {deptProgramData.length > 0 && (() => {
            // Parse "dept|program" group_key and group by department
            const deptMap = {};
            deptProgramData.forEach(row => {
              const parts = (row.group_key || '').split('|');
              const dept = parts[0] || 'Unknown';
              const program = parts[1] || '-';
              if (!deptMap[dept]) deptMap[dept] = [];
              deptMap[dept].push({ ...row, _dept: dept, _program: program });
            });
            const sortedDepts = Object.entries(deptMap).sort((a, b) => {
              const aTotal = a[1].reduce((s, row) => s + (Number(row.total_prod_rev) || 0), 0);
              const bTotal = b[1].reduce((s, row) => s + (Number(row.total_prod_rev) || 0), 0);
              return bTotal - aTotal;
            });

            const sumDeptRows = (rows) => {
              const fields = ['total_prod_rev','total_prod_cost_fob','total_prod_royalty','invoiced_rev','invoiced_cost','invoiced_royalty','pick_rev','pick_cost','pick_royalty','ats_rev','ats_cost','ats_royalty','wip_rev','wip_cost_fob','wip_royalty'];
              return fields.reduce((acc, f) => { acc[f] = rows.reduce((s, row) => s + (Number(row[f]) || 0), 0); return acc; }, {});
            };

            const renderCells = (d, bold = false) => {
              const cls = bold ? 'font-semibold' : '';
              const tp = d.total_prod_rev || 0, tc = d.total_prod_cost_fob || 0, tr2 = d.total_prod_royalty || 0;
              const ir = d.invoiced_rev || 0, ic = d.invoiced_cost || 0, iry = d.invoiced_royalty || 0;
              const pr = d.pick_rev || 0, pc = d.pick_cost || 0, pry = d.pick_royalty || 0;
              const ar = d.ats_rev || 0, ac = d.ats_cost || 0, ary = d.ats_royalty || 0;
              const wr = d.wip_rev || 0, wc = d.wip_cost_fob || 0, wry = d.wip_royalty || 0;
              return (<>
                <td className={`px-2 py-1.5 text-right font-plex-mono tabular-nums text-blue-700 ${cls}`}>{formatCurrency(tp)}</td>
                <td className={`px-2 py-1.5 text-right font-plex-mono tabular-nums ${gpColor(calcGP(tp, tc, tr2))} ${cls}`}>{formatPct(calcGP(tp, tc, tr2))}</td>
                <td className={`px-2 py-1.5 text-right font-plex-mono tabular-nums text-green-700 ${cls}`}>{formatCurrency(ir)}</td>
                <td className={`px-2 py-1.5 text-right font-plex-mono tabular-nums ${gpColor(calcGP(ir, ic, iry))} ${cls}`}>{formatPct(calcGP(ir, ic, iry))}</td>
                <td className={`px-2 py-1.5 text-right font-plex-mono tabular-nums text-orange-700 ${cls}`}>{formatCurrency(pr)}</td>
                <td className={`px-2 py-1.5 text-right font-plex-mono tabular-nums ${gpColor(calcGP(pr, pc, pry))} ${cls}`}>{formatPct(calcGP(pr, pc, pry))}</td>
                <td className={`px-2 py-1.5 text-right font-plex-mono tabular-nums text-teal-700 ${cls}`}>{formatCurrency(ar)}</td>
                <td className={`px-2 py-1.5 text-right font-plex-mono tabular-nums ${gpColor(calcGP(ar, ac, ary))} ${cls}`}>{formatPct(calcGP(ar, ac, ary))}</td>
                <td className={`px-2 py-1.5 text-right font-plex-mono tabular-nums text-amber-700 ${cls}`}>{formatCurrency(wr)}</td>
                <td className={`px-2 py-1.5 text-right font-plex-mono tabular-nums ${gpColor(calcGP(wr, wc, wry))} ${cls}`}>{formatPct(calcGP(wr, wc, wry))}</td>
              </>);
            };

            const grandTotal = sumDeptRows(deptProgramData);

            return (
              <div className="bg-carbon-white border border-carbon-border overflow-hidden">
                <div className="px-4 py-3 bg-carbon-gray-10 border-b border-carbon-border">
                  <h3 className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold text-carbon-ink">By Department / Program</h3>
                  <p className="font-plex-mono text-[10px] text-carbon-gray-50 mt-0.5">{sortedDepts.length} departments, {deptProgramData.length} rows</p>
                </div>
                <div className="overflow-x-auto max-h-[500px]">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-carbon-gray-10 z-10">
                      <tr>
                        <th className="px-3 py-2 text-left font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border">Dept / Program</th>
                        <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-blue-700 border-b border-carbon-border">Total Prod $</th>
                        <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-blue-600 border-b border-carbon-border">GP%</th>
                        <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-green-700 border-b border-carbon-border">Invoice $</th>
                        <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-green-600 border-b border-carbon-border">GP%</th>
                        <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-orange-700 border-b border-carbon-border">Pick $</th>
                        <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-orange-600 border-b border-carbon-border">GP%</th>
                        <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-teal-700 border-b border-carbon-border">ATS $</th>
                        <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-teal-600 border-b border-carbon-border">GP%</th>
                        <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-amber-700 border-b border-carbon-border">WIP $</th>
                        <th className="px-2 py-2 text-right font-plex-mono text-[10px] tracking-[0.1em] uppercase font-normal text-amber-600 border-b border-carbon-border">GP%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedDepts.map(([dept, rows]) => {
                        const deptSub = sumDeptRows(rows);
                        const sortedRows = [...rows].sort((a, b) => (Number(b.total_prod_rev) || 0) - (Number(a.total_prod_rev) || 0));
                        return (
                          <React.Fragment key={dept}>
                            <tr className="bg-carbon-gray-10 border-t border-carbon-border">
                              <td className="px-3 py-1.5 font-semibold text-carbon-ink">Dept {dept}</td>
                              {renderCells(deptSub, true)}
                            </tr>
                            {sortedRows.map((row, idx) => (
                              <tr key={idx} className="border-b border-carbon-gray-10 hover:bg-carbon-gray-10">
                                <td className="px-3 py-1.5 pl-8 text-carbon-gray-70">{row._program || '-'}</td>
                                {renderCells(row)}
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                    <tfoot className="sticky bottom-0 bg-carbon-gray-10 border-t-2 border-carbon-border">
                      <tr className="font-semibold text-carbon-ink">
                        <td className="px-3 py-2">TOTAL</td>
                        {renderCells(grandTotal, true)}
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            );
          })()}
        </>
      )}

      {!loading && data.length === 0 && !error && (
        <div className="bg-carbon-white border border-carbon-border text-center py-12">
          <p className="font-plex-mono text-xs text-carbon-gray-50">Click "Load Data" to fetch {customerName} production vs sales data.</p>
        </div>
      )}
    </div>
  );
}
